/**
 * /admin/metrics - cross-tenant platform metrics (CHAP-173 / Phase 1N #145).
 *
 * Read-only, admin-gated. Totals (tenants / MAU / docs / votes / MRR) plus a
 * per-tenant drill-down. MRR is $0 until billing (CHAP-106) lands.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api.js';

interface Metrics {
  totals: {
    tenants: number;
    mau: number;
    docs: number;
    votesCast: number;
    mrrCents: number;
    mrrFormatted: string;
  };
  perTenant: Array<{
    slug: string;
    displayName: string;
    status: string;
    members: number;
    docs: number;
    votesCast: number;
    mrrCents: number;
  }>;
  generatedAt: string;
}

export default function AdminMetrics() {
  const { data, isLoading, error, refetch, isRefetching } = useQuery<Metrics, Error>({
    queryKey: ['admin-metrics'],
    queryFn: () => apiGet<Metrics>('/api/admin/metrics'),
    refetchInterval: 5 * 60_000,
  });

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Platform Metrics</h2>
            <p className="text-neutral text-sm mt-1">
              Cross-tenant overview. Per CHAP-173.{' '}
              {data?.generatedAt && (
                <span className="text-xs">
                  Last updated {new Date(data.generatedAt).toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="text-sm font-medium px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 flex-shrink-0"
          >
            {isRefetching ? '…' : 'Refresh'}
          </button>
        </div>
      </header>

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
          Loading metrics…
        </div>
      )}
      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
          Failed to load: {error.message}
        </div>
      )}

      {data && (
        <>
          <section className="grid grid-cols-2 lg:grid-cols-5 gap-3 min-w-0">
            <Stat label="Tenants" value={data.totals.tenants.toLocaleString()} />
            <Stat label="MAU (30d)" value={data.totals.mau.toLocaleString()} />
            <Stat label="Data-room docs" value={data.totals.docs.toLocaleString()} />
            <Stat label="Votes cast" value={data.totals.votesCast.toLocaleString()} />
            <Stat label="MRR" value={data.totals.mrrFormatted} />
          </section>

          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
            <h3 className="text-navy text-sm font-semibold mb-3">Per-tenant drill-down</h3>
            <div className="min-w-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-neutral">
                  <tr className="border-b border-gray-100">
                    <th className="pb-2 pr-3 font-medium">Tenant</th>
                    <th className="pb-2 pr-3 font-medium">Status</th>
                    <th className="pb-2 pr-3 text-right font-medium">Members</th>
                    <th className="pb-2 pr-3 text-right font-medium">Docs</th>
                    <th className="pb-2 pr-3 text-right font-medium">Votes</th>
                    <th className="pb-2 text-right font-medium">MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {data.perTenant.map((t) => (
                    <tr key={t.slug} className="border-b border-gray-50">
                      <td className="py-1.5 pr-3">
                        <span className="font-medium text-navy">{t.displayName}</span>{' '}
                        <span className="font-mono text-xs text-neutral">/{t.slug}</span>
                      </td>
                      <td className="py-1.5 pr-3 text-xs">{t.status}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">{t.members}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">{t.docs}</td>
                      <td className="py-1.5 pr-3 text-right font-mono">{t.votesCast}</td>
                      <td className="py-1.5 text-right font-mono">${(t.mrrCents / 100).toLocaleString()}</td>
                    </tr>
                  ))}
                  {data.perTenant.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-3 text-center text-neutral text-xs">
                        No tenants yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 min-w-0">
      <div className="text-neutral text-xs">{label}</div>
      <div className="text-navy text-2xl font-bold mt-1 truncate">{value}</div>
    </div>
  );
}
